import {
  IconButton,
  Button,
  Grid,
  Badge,
  Flex,
  Text,
  useDisclosure,
  useColorModeValue,
  Divider,
  GridItem,
  Tooltip,
  Box,
} from '@chakra-ui/react';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';
import InfoDrawer from '../../common/InfoDrawer';
import { Link } from 'react-router-dom';
import MoreInfoLine from '../../common/MoreInfoLine';
import MessageStatus from './MessageStatus';
import { CustomCsvDownloadIcon } from '../../../theme/icons/CustomCsvDownloadIcon';
import MessageBatchResultDownload from './MessageBatchResultDownload';
import SendNowModal from '../../modals/SendNowModal';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

function MessageBatchMoreInfo({
  cell,
  cancel,
  fetchData,
  pageIndex,
  pageSize,
  cancelDisabled,
  customTriggerButtonComponent,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isReadOnly = useIsReadOnly();

  const modeIconColor = useColorModeValue('secondaryDark08', '#DDDDDD');
  const modeBoxBg = useColorModeValue('white', 'secondaryDark');
  const modeTextBg = useColorModeValue('white', 'primaryDark06');
  const modeLabelBg = useColorModeValue('white', 'primaryDark03');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');

  function handleMoreInfoClick(e) {
    e.stopPropagation();
    onOpen();
  }

  const bgColors = {
    modeBoxBg,
    modeTextBg,
    modeBorderColor,
    modeLabelBg,
  };

  const {
    isOpen: isSendNowOpen,
    onOpen: onSendNowOpen,
    onClose: onSendNowClose,
  } = useDisclosure();

  return (
    <>
      {customTriggerButtonComponent ? (
        customTriggerButtonComponent(onOpen)
      ) : (
        <IconButton
          aria-label="More Info"
          onClick={handleMoreInfoClick}
          icon={
            <CustomChevronLeftIcon
              w="20px"
              h="20px"
              color={modeIconColor}
              transform="rotate(180deg)"
            />
          }
          variant="white"
          size="sm"
          w="36px"
          mr={1}
        />
      )}
      <InfoDrawer
        isOpen={isOpen}
        onClose={onClose}
        header="Message Batch Overview"
        footer={
          <Flex justifyContent="space-between" alignItems="center" w="full">
            <Link
              color={useColorModeValue('secondaryDark08', '#DDDDDD')}
              fontSize="14px"
              onClick={onClose}
              mr="30px"
            >
              <Text textStyle="headingFamilyMedium">Back to list</Text>
            </Link>
            {cell.row.original.status === 'scheduled' && (
              <Tooltip
                label="You can cancel messages up to 30 minutes before the scheduled time."
                hasArrow
                placement="top"
                fontSize="12px"
                bg="inkBlue"
                color="white"
                borderRadius="6px"
                textAlign="center"
                p="10px"
              >
                <Box as="span" display="inline-block" pointerEvents="auto">
                  <Button
                    size="sm"
                    isDisabled={cancelDisabled}
                    onClick={() =>
                      cancel(
                        cell.row.original.id,
                        cell.row.original.messageType
                      )
                    }
                  >
                    Cancel
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Flex>
        }
        bgColors={bgColors}
      >
        <Grid templateColumns={{ base: '150px 1fr', md: '192px 1fr' }} gap={0}>
          <MoreInfoLine heading="Batch Name" value={cell.row.original.name} />
          <MoreInfoLine heading="Batch ID" value={cell.row.original.id} />
          {cell.row.original.tag && (
            <MoreInfoLine
              heading="Tag"
              value={
                cell.row.original.tag ? (
                  <Badge colorScheme="purple">{cell.row.original.tag}</Badge>
                ) : null
              }
            />
          )}
          <MoreInfoLine
            heading="Message Type"
            value={cell.row.original.messageType}
          />
          <MoreInfoLine
            heading="Status"
            value={<MessageStatus status={cell.row.original.status} />}
          />
          {cell.row.original.messageType !== 'Wallet' &&
            (cell.row.original.status === 'failed' ||
              cell.row.original.status === 'cancellation_failed' ||
              cell.row.original.status === 'partially_cancelled') && (
              <MoreInfoLine
                heading="Error Message"
                value={cell.row.original.errorMessage}
              />
            )}
          {cell.row.original.messageType === 'Wallet' &&
            cell.row.original.errorsCsv && (
              <MoreInfoLine
                heading="Error CSV"
                value={
                  <Link to={cell.row.original.errorsCsv}>
                    <CustomCsvDownloadIcon color="brand" boxSize={7} />
                  </Link>
                }
              />
            )}
          {(cell.row.original.completedAt ||
            [
              'cancelled',
              'partially_cancelled',
              'cancellation_failed',
            ].includes(cell.row.original.status)) && (
            <MoreInfoLine
              heading="Delivery Report CSV"
              value={
                <MessageBatchResultDownload batchId={cell.row.original.id} />
              }
            />
          )}
          <MoreInfoLine heading="Content" value={cell.row.original.content} />
          {cell.row.original.messageType === 'mms' && (
            <MoreInfoLine
              heading="Media URL"
              value={cell.row.original.mediaUrl}
            />
          )}
          <MoreInfoLine
            heading="Total Recipients"
            value={cell.row.original.recipients}
          />
          {cell.row.original.errors > 0 && (
            <MoreInfoLine
              heading="Total Errors"
              value={cell.row.original.errors}
            />
          )}
          {cell.row.original.scheduledAt && (
            <MoreInfoLine
              heading="Scheduled At"
              value={cell.row.original.scheduledAt}
            />
          )}
          {cell.row.original.completedAt && (
            <MoreInfoLine
              heading="Completed At"
              value={cell.row.original.completedAt}
            />
          )}

          {cell.row.original.status === 'scheduled' &&
            cell.row.original.messageType === 'Wallet' && (
              <>
                <GridItem colSpan={2}>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    padding="16px 0px 16px 22px"
                    flexWrap="wrap"
                  >
                    {isReadOnly ? (
                      <LockedActionButton
                        variant="secondary"
                        size="sm"
                        mr="20px"
                        ml="auto"
                      >
                        Send Now
                      </LockedActionButton>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        alt="Send Now"
                        mr="20px"
                        ml="auto"
                        onClick={onSendNowOpen}
                      >
                        Send Now
                      </Button>
                    )}
                  </Flex>
                </GridItem>

                <GridItem colSpan={2} w="full">
                  <Divider borderColor={modeBorderColor} />
                </GridItem>
              </>
            )}
        </Grid>
      </InfoDrawer>
      <SendNowModal
        isOpen={isSendNowOpen}
        onClose={onSendNowClose}
        messageBatchId={cell.row.original.id}
        messageBatchName={cell.row.original.name}
        scheduledTime={cell.row.original.scheduledAt}
        fetchData={fetchData}
        pageIndex={pageIndex}
        pageSize={pageSize}
      />
    </>
  );
}

export default MessageBatchMoreInfo;
